import { Flex } from 'antd';
import { type FC, type PropsWithChildren, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { Alert } from '@/components/alert';
import { FormattedMessage, useIntl } from '@/components/formatted_message/FormattedMessage';
import { Typography } from '@/components/typography';
import { useAuthContext } from '@/contexts/AuthContext';
import { EnmonLogo } from '@/routes/auth/components/EnmonLogo';
import { AuthLayout as messages } from '../messages';
import { StyledAuthLayout } from './Containers';
import { StyledFormWrapper } from './FormContainer';

const { Headline, Paragraph } = Typography;

export const AuthLayout: FC<PropsWithChildren> = ({ children }) => {
  const intl = useIntl();
  const { isError: hasError, error, identifiedUser } = useAuthContext();

  const errorMessage = useMemo(() => {
    if (!hasError) return undefined;

    if (error.login) {
      return intl.formatMessage(messages.loginError);
    }

    if (error.identify) {
      return intl.formatMessage(messages.identifyError);
    }

    const { emailPassword, oneTimePassword } = identifiedUser.methods ?? {};

    if (identifiedUser.identifier && !(emailPassword || oneTimePassword)) {
      return intl.formatMessage(messages.userNotFound);
    }

    return undefined;
  }, [hasError, error, intl, identifiedUser]);

  return (
    <StyledAuthLayout>
      <Helmet>
        <title>{intl.formatMessage(messages.title)}</title>
      </Helmet>
      <EnmonLogo />
      <Flex gap={24} vertical>
        <Flex vertical gap={8}>
          <Headline>
            <FormattedMessage {...messages.title} />
          </Headline>
          {errorMessage ? (
            <Alert description={errorMessage} showIcon style={{ marginBottom: 0 }} type="error" />
          ) : (
            <Paragraph>
              <FormattedMessage {...messages.subTitle} />
            </Paragraph>
          )}
        </Flex>
        <StyledFormWrapper>{children}</StyledFormWrapper>
      </Flex>
    </StyledAuthLayout>
  );
};
